import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { getTone, grey, greyAlpha, lightVeil, withAlpha } from '../../../theme/tones';
import { gradients, FONT_DISPLAY } from '../../../theme/tokens';

/**
 * « L'application en apesanteur ».
 *
 * Quatre fragments d'interface — une fiche session, un calendrier de
 * vacations, une jauge de satisfaction et une feuille d'émargement — posés
 * en perspective sur le panneau de marque et animés lentement. Ce sont des
 * silhouettes du produit, pas des captures : aucune donnée, uniquement le
 * vocabulaire métier (statuts, financeurs) et les tons canoniques.
 *
 * Le parent transmet deux valeurs de rotation (parallaxe souris) ; ici on
 * ne gère que la matière et la flottaison.
 */

const orange = getTone('orange').light;

/* ------------------------------------------------------------ Atomes -- */

/** Ligne « squelette » : remplace un texte sans en simuler un vrai. */
const Line = ({ w = '60%', h = 7, strong = false }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: '999px',
      backgroundColor: lightVeil(strong ? 0.42 : 0.2)
    }}
  />
);

Line.propTypes = {
  w: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  h: PropTypes.number,
  strong: PropTypes.bool
};

/** Pastille de statut lisible sur fond marine (la Pill du kit est faite
 *  pour les surfaces claires : texte foncé sur teinte à 9 %). */
const DarkPill = ({ label, tone = 'neutral', mono = false }) => {
  const t = getTone(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.6,
        px: 1,
        py: 0.4,
        borderRadius: '8px',
        fontSize: mono ? '0.6rem' : '0.64rem',
        fontWeight: 700,
        letterSpacing: mono ? '0.08em' : '0.01em',
        lineHeight: 1.2,
        color: t.light,
        backgroundColor: withAlpha(t.base, 0.16),
        border: `1px solid ${withAlpha(t.base, 0.34)}`,
        fontFamily: mono ? '"JetBrains Mono", "Roboto Mono", monospace' : 'inherit',
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        component="span"
        sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: t.light, flexShrink: 0 }}
      />
      {label}
    </Box>
  );
};

DarkPill.propTypes = {
  label: PropTypes.node,
  tone: PropTypes.string,
  mono: PropTypes.bool
};

/** Titre de tuile : petites capitales espacées, comme les en-têtes de l'app. */
const TileTitle = ({ children }) => (
  <Box
    sx={{
      fontFamily: FONT_DISPLAY,
      fontSize: '0.62rem',
      fontWeight: 700,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: lightVeil(0.55),
      mb: 1.2
    }}
  >
    {children}
  </Box>
);

TileTitle.propTypes = { children: PropTypes.node };

/**
 * Tuile de verre.
 *
 * `depth` = profondeur (translateZ) : plus la valeur est grande, plus la
 * tuile paraît proche et plus elle bouge avec la souris. `delay` décale
 * l'apparition et le flottement pour que rien ne bouge en cadence.
 */
const Tile = ({ children, sx = {}, depth = 0, delay = 0, floatDuration = 7, reduce }) => (
  <Box
    component={motion.div}
    initial={reduce ? false : { opacity: 0, y: 40, scale: 0.94 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ type: 'spring', stiffness: 120, damping: 20, delay: 0.35 + delay }}
    style={{ z: depth, transformStyle: 'preserve-3d' }}
    sx={{ position: 'absolute', ...sx }}
  >
    <Box
      component={motion.div}
      animate={reduce ? undefined : { y: [0, -9, 0] }}
      transition={{ duration: floatDuration, repeat: Infinity, ease: 'easeInOut', delay }}
      sx={{
        width: '100%',
        borderRadius: '18px',
        p: 2,
        color: grey(0),
        backgroundColor: lightVeil(0.07),
        border: `1px solid ${lightVeil(0.14)}`,
        boxShadow: `0 36px 70px -28px ${greyAlpha(950, 0.85)}, inset 0 1px 0 ${lightVeil(0.14)}`,
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)'
      }}
    >
      {children}
    </Box>
  </Box>
);

Tile.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  depth: PropTypes.number,
  delay: PropTypes.number,
  floatDuration: PropTypes.number,
  reduce: PropTypes.bool
};

/* ----------------------------------------------------------- Tuiles -- */

const SessionTile = () => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: 1.8 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: '11px',
          background: gradients.accent,
          boxShadow: `0 10px 20px -10px ${getTone('blue').glow}`
        }}
      />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.9 }}>
        <Line
          w="72%"
          h={8}
          strong
        />
        <Line w="46%" />
      </Box>
    </Box>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 2 }}>
      <DarkPill
        label="Inscrit"
        tone="emerald"
      />
      <DarkPill
        label="En attente"
        tone="amber"
      />
      <DarkPill
        label="Convention signée"
        tone="teal"
      />
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
      <TileTitle>Remplissage</TileTitle>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.2 }}>
        <DarkPill
          label="DPC"
          tone="blue"
          mono
        />
        <DarkPill
          label="FIF-PL"
          tone="violet"
          mono
        />
      </Box>
    </Box>
    <Box sx={{ height: 8, borderRadius: '999px', backgroundColor: lightVeil(0.12), overflow: 'hidden' }}>
      <Box
        component={motion.div}
        initial={{ width: '0%' }}
        animate={{ width: '74%' }}
        transition={{ duration: 1.4, delay: 1, ease: [0.16, 1, 0.3, 1] }}
        sx={{ height: '100%', borderRadius: '999px', background: gradients.aurora }}
      />
    </Box>
  </>
);

/* Hauteurs des barres : silhouette d'une semaine, la vacation en orange. */
const WEEK = [
  { day: 'L', h: 34 },
  { day: 'M', h: 52 },
  { day: 'M', h: 26 },
  { day: 'J', h: 64, live: true },
  { day: 'V', h: 44 },
  { day: 'S', h: 18 },
  { day: 'D', h: 12 }
];

const CalendarTile = ({ reduce }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <TileTitle>Vacations</TileTitle>
      <Box sx={{ mb: 1.2 }}>
        <DarkPill
          label="Jeudi"
          tone="orange"
        />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.9, height: 72 }}>
      {WEEK.map((d, i) => (
        <Box
          key={`${d.day}-${d.h}`}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.6, height: '100%', justifyContent: 'flex-end' }}
        >
          <Box
            component={motion.div}
            initial={reduce ? false : { height: 0 }}
            animate={{ height: d.h }}
            transition={{ duration: 0.9, delay: 0.9 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
            sx={{
              width: '100%',
              borderRadius: '5px',
              background: d.live
                ? `linear-gradient(180deg, ${orange} 0%, ${getTone('orange').base} 100%)`
                : lightVeil(0.2),
              boxShadow: d.live ? `0 10px 22px -8px ${withAlpha(orange, 0.7)}` : 'none'
            }}
          />
          <Box sx={{ fontSize: '0.56rem', fontWeight: 700, color: lightVeil(d.live ? 0.9 : 0.4), lineHeight: 1 }}>
            {d.day}
          </Box>
        </Box>
      ))}
    </Box>
  </>
);

CalendarTile.propTypes = { reduce: PropTypes.bool };

const RingTile = ({ reduce }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <TileTitle>Satisfaction</TileTitle>
    <Box sx={{ position: 'relative', width: 78, height: 78 }}>
      <Box
        component="svg"
        viewBox="0 0 80 80"
        sx={{ width: '100%', height: '100%', transform: 'rotate(-90deg)', display: 'block' }}
      >
        <defs>
          <linearGradient
            id="login-ring-gradient"
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop
              offset="0"
              stopColor={getTone('blue').light}
            />
            <stop
              offset="0.6"
              stopColor={getTone('violet').light}
            />
            <stop
              offset="1"
              stopColor={getTone('sky').light}
            />
          </linearGradient>
        </defs>
        <circle
          cx="40"
          cy="40"
          r="31"
          fill="none"
          stroke={lightVeil(0.12)}
          strokeWidth="8"
        />
        <motion.circle
          cx="40"
          cy="40"
          r="31"
          fill="none"
          stroke="url(#login-ring-gradient)"
          strokeWidth="8"
          strokeLinecap="round"
          initial={reduce ? false : { pathLength: 0 }}
          animate={{ pathLength: 0.92 }}
          transition={{ duration: 1.6, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: grey(0)
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: 26 }} />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 0.35, mt: 1.4 }}>
      {['a', 'b', 'c', 'd', 'e'].map((star) => (
        <Box
          key={star}
          sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: getTone('amber').light }}
        />
      ))}
    </Box>
  </Box>
);

RingTile.propTypes = { reduce: PropTypes.bool };

/* Un paraphe : la signature qui clôt la feuille d'émargement. */
const SIGNATURE_PATH = 'M8 34 C 10 14, 20 6, 24 20 C 27 32, 16 42, 20 30 C 24 16, 36 4, 46 16 S 58 44, 70 24 C 76 12, 86 10, 92 22 S 104 44, 116 24 C 122 12, 132 14, 138 26 S 150 38, 158 24 C 162 18, 168 20, 174 22';

const SignatureTile = ({ reduce }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <TileTitle>Émargement</TileTitle>
      <Box sx={{ mb: 1.2 }}>
        <DarkPill
          label="Signée"
          tone="emerald"
        />
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, mb: 1.2 }}>
      <Line w="58%" />
      <Line w="38%" />
    </Box>
    <Box
      sx={{
        borderRadius: '10px',
        border: `1px dashed ${lightVeil(0.22)}`,
        backgroundColor: lightVeil(0.04),
        px: 1,
        py: 0.5
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 180 48"
        sx={{ width: '100%', height: 44, display: 'block' }}
      >
        <motion.path
          d={SIGNATURE_PATH}
          fill="none"
          stroke={orange}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={reduce ? false : { pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ pathLength: { duration: 1.8, delay: 1.5, ease: 'easeInOut' }, opacity: { duration: 0.2, delay: 1.5 } }}
        />
      </Box>
    </Box>
  </>
);

SignatureTile.propTypes = { reduce: PropTypes.bool };

/* ------------------------------------------------------------ Scène -- */

/**
 * @param {MotionValue} rotateX — inclinaison verticale (parallaxe souris)
 * @param {MotionValue} rotateY — inclinaison horizontale
 */
const LoginShowcase = ({ rotateX, rotateY }) => {
  const reduce = Boolean(useReducedMotion());

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 500,
        height: 436,
        position: 'relative',
        perspective: '1400px',
        // La scène est dessinée à taille fixe puis réduite sur les écrans
        // moyens : la composition garde ses proportions.
        transform: { md: 'scale(0.74)', lg: 'scale(0.92)', xl: 'scale(1)' },
        transformOrigin: 'center center'
      }}
    >
      <Box
        component={motion.div}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        sx={{ position: 'absolute', inset: 0 }}
      >
        <Tile
          reduce={reduce}
          depth={40}
          delay={0}
          floatDuration={8}
          sx={{ left: 60, top: 70, width: 300 }}
        >
          <SessionTile />
        </Tile>

        <Tile
          reduce={reduce}
          depth={90}
          delay={0.15}
          floatDuration={6.5}
          sx={{ left: 290, top: 0, width: 210 }}
        >
          <CalendarTile reduce={reduce} />
        </Tile>

        <Tile
          reduce={reduce}
          depth={120}
          delay={0.3}
          floatDuration={7.5}
          sx={{ left: 0, top: 262, width: 150 }}
        >
          <RingTile reduce={reduce} />
        </Tile>

        <Tile
          reduce={reduce}
          depth={70}
          delay={0.45}
          floatDuration={9}
          sx={{ left: 235, top: 280, width: 250 }}
        >
          <SignatureTile reduce={reduce} />
        </Tile>
      </Box>
    </Box>
  );
};

LoginShowcase.propTypes = {
  rotateX: PropTypes.object,
  rotateY: PropTypes.object
};

export default LoginShowcase;
